
import type { CurationToolRuleType, RelatedTags } from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref, SetupContext } from 'vue'
import type { Route } from 'vue-router'
import type {
  IToolingListComposable,
  IToolingListComposableProps,
} from '../../composables/use-tooling-list'
import type {
  DataTableColumn,
  DataTableOption,
  GuardResolveFunction,
  ToolMessages,
} from '../../models'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import { EpbBadge } from '../../components/shared/epb-badge'
import { useToolingList } from '../../composables/use-tooling-list'
import { AlignEnum, ColorEnum, FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'

export interface RelatedTagsListApi extends IToolingListComposable<RelatedTags> {
  baseClass: string
  routes: {
    create: string
    update: string
  }
  messages: ToolMessages
  columns: ComputedRef<Array<DataTableColumn>>
  getRuleCountByType: (dataItem: RelatedTags, type: CurationToolRuleType) => number
  ColorEnum
  crudListRef: Ref
  onOptionsClick: (option: DataTableOption, dataItem) => void
}

export default defineComponent({
  name: 'RelatedTagsList',
  components: {
    EpbBadge,
    CrudList,
    DataTable,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  setup(props: IToolingListComposableProps, ctx: SetupContext): RelatedTagsListApi {
    const baseClass = 'epb-related-tags-list'

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_TAGS]

    const currentInstance = getCurrentInstance()
    const messages: ToolMessages = currentInstance.proxy.$epb.messages.tools.relatedTags

    const toolingList = useToolingList<RelatedTags>(props, ctx, {
      routes,
      messages,
    })

    const columns = computed<Array<DataTableColumn>>(() => {
      return [
        {
          id: 'criteria.queries',
          sortable: true,
          sortableId: 'query',
          text: messages.fields.query.label,
          unwrapText: true,
          copyToClipboard: true,
        },
        ...toolingList.visiblePivotColumns.value,
        {
          id: 'data.rules.added',
          text: messages.fields.rules.columns.added.label,
        },
        {
          id: 'data.rules.removed',
          text: messages.fields.rules.columns.removed.label,
        },
        {
          id: 'metadata.updated',
          sortable: true,
          sortableId: 'updated',
          text: messages.fields.updateDate.label,
        },
        {
          id: 'metadata.author',
          text: messages.fields.user.label,
        },
        {
          id: 'criteria.enabled',
          text: messages.fields.enabled.label,
        },
        {
          id: 'options',
          width: '44px',
          align: AlignEnum.CENTER,
        },
      ]
    })

    function getRuleCountByType(dataItem: RelatedTags, type: CurationToolRuleType): number {
      return dataItem.data.rules.filter(rule => rule.type === type).length
    }

    toolingList.loadData()

    const crudListRef = ref(null)

    function onOptionsClick(option: DataTableOption, dataItem): void {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    return {
      ...toolingList,
      baseClass,
      routes,
      messages,
      columns,
      getRuleCountByType,
      ColorEnum,
      crudListRef,
      onOptionsClick,
    }
  },
})
